import React, { useState } from 'react';
import { useStyletron } from 'baseui';
import { Modal, ModalHeader, ModalBody, ModalFooter, ModalButton, SIZE } from 'baseui/modal';
import { FormControl } from 'baseui/form-control';
import { DatePicker } from 'baseui/datepicker';
import { TimePicker } from 'baseui/timepicker';
import { LabelMedium } from 'baseui/typography';

interface ScheduleDialogProps {
	isOpen: boolean;
	onClose: () => void;
	onSubmit: (timestamp: number) => void;
}

const ScheduleDialog: React.FC<ScheduleDialogProps> = ({ isOpen, onClose, onSubmit }) => {
	const [css] = useStyletron();
	const [date, setDate] = useState<Date[]>([new Date()]);
	const [time, setTime] = useState<Date>(new Date());

	const handleSubmit = () => {
		if (date.length === 0) return;

		// Combine date and time into a single Date object
		const scheduledDate = new Date(date[0]);
		scheduledDate.setHours(time.getHours(), time.getMinutes(), 0, 0);

		// Convert to Unix timestamp (milliseconds)
		const timestamp = scheduledDate.getTime();

		onSubmit(timestamp);
	};

	// Handle date change with auto-close
	const handleDateChange = ({ date: selectedDate }) => {
		setDate(Array.isArray(selectedDate) ? selectedDate : [selectedDate]);
		// Force close the calendar by triggering blur
		setTimeout(() => {
			const { activeElement } = document;
			if (activeElement) {
				(activeElement as HTMLElement).blur();
			}
		}, 100);
	};

	// Handle time change
	const handleTimeChange = (selectedTime: Date | null) => {
		if (selectedTime) {
			setTime(selectedTime);
		}
	};

	return (
		<Modal onClose={onClose} isOpen={isOpen} animate autoFocus size={SIZE.default} role='dialog'>
			<ModalHeader>Schedule Request</ModalHeader>
			<ModalBody>
				<div className={css({ marginBottom: '20px' })}>
					<LabelMedium>Select date and time for the request to be processed</LabelMedium>
				</div>

				<FormControl label='Date'>
					<DatePicker
						value={date}
						onChange={handleDateChange}
						minDate={new Date()}
						overrides={{
							Calendar: {
								props: {
									autoFocusCalendar: false
								}
							},
							Input: {
								props: {
									overrides: {
										Root: {
											style: { width: '100%' }
										}
									}
								}
							}
						}}
					/>
				</FormControl>

				<FormControl label='Time'>
					<TimePicker
						value={time}
						onChange={handleTimeChange}
						creatable
						format='12'
						overrides={{
							Select: {
								props: {
									creatable: true,
									openOnClick: true
								}
							}
						}}
					/>
				</FormControl>
			</ModalBody>
			<ModalFooter>
				<ModalButton kind='tertiary' onClick={onClose}>
					Cancel
				</ModalButton>
				<ModalButton onClick={handleSubmit}>Schedule</ModalButton>
			</ModalFooter>
		</Modal>
	);
};

export default ScheduleDialog;
